import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  isValidSlug,
  type BoothCaptureSummary,
  type BoothManifest,
} from '@photobooth/shared';
import { fetchBoothCaptures, fetchBoothManifest } from '../api/client';
import { Button } from '../ui/Button';
import { QrOverlay } from '../ui/QrOverlay';
import { isUnlocked, manifestRequiresPassword } from '../builder/passwordGate';
import { PasswordGate } from '../ui/PasswordGate';
import { logger } from '../utils/log';

const log = logger('wall');
const POLL_INTERVAL_MS = 8_000;

type Phase = 'loading' | 'ready' | 'not-found' | 'expired' | 'error';

/**
 * /<slug>/wall — auto-refreshing grid meant to be projected on a TV at
 * the event. New captures animate in. Press F (or the button) to enter
 * fullscreen. No header chrome inside fullscreen.
 */
export function WallPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<BoothManifest | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [phase, setPhase] = useState<Phase>('loading');
  const [captures, setCaptures] = useState<BoothCaptureSummary[]>([]);
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(new Set());
  const [qrVisible, setQrVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Track which capture IDs we've already seen so we can mark new ones for
  // the fade-in animation.
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    if (!isValidSlug(slug)) {
      setPhase('not-found');
      return;
    }
    (async () => {
      try {
        const m = await fetchBoothManifest(slug);
        if (cancelled) return;
        if (!m) {
          setPhase('not-found');
          return;
        }
        if (new Date(m.expiresAt).getTime() < Date.now()) {
          setPhase('expired');
          return;
        }
        setManifest(m);
        if (!manifestRequiresPassword(m) || isUnlocked(m.slug)) {
          setUnlocked(true);
        }
        setPhase('ready');
      } catch (err) {
        log.warn('manifest load failed', err);
        if (!cancelled) setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchBoothCaptures(slug);
      const seen = seenIdsRef.current;
      const newIds = new Set<string>();
      for (const c of res.captures) {
        if (!seen.has(c.id)) {
          newIds.add(c.id);
          seen.add(c.id);
        }
      }
      setCaptures(res.captures);
      if (newIds.size > 0) {
        setRecentlyAddedIds(newIds);
        window.setTimeout(() => setRecentlyAddedIds(new Set()), 2500);
      }
    } catch (err) {
      log.warn('refresh failed', err);
    }
  }, [slug]);

  useEffect(() => {
    if (phase !== 'ready' || !unlocked) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [phase, unlocked, refresh]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'f' || e.key === 'F') void toggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await el.requestFullscreen?.();
    }
  };

  if (phase === 'loading') {
    return <Centered>Loading…</Centered>;
  }
  if (phase === 'not-found') {
    return (
      <Centered>
        <h1>No such booth</h1>
        <Button variant="primary" onClick={() => navigate('/builder')}>
          Build your own
        </Button>
      </Centered>
    );
  }
  if (phase === 'expired') {
    return (
      <Centered>
        <h1>This booth has expired</h1>
      </Centered>
    );
  }
  if (phase === 'error') {
    return <Centered>Couldn’t load.</Centered>;
  }
  if (manifest && !unlocked) {
    return <PasswordGate manifest={manifest} onUnlocked={() => setUnlocked(true)} />;
  }

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: '100dvh',
        background: '#000',
        color: '#fff',
        padding: isFullscreen ? 16 : 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {!isFullscreen && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 24 }}>{manifest?.title ?? slug}</h1>
            <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
              Live wall · auto-refreshing every {POLL_INTERVAL_MS / 1000}s
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={() => setQrVisible(true)}>
              Show QR
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/${slug}/gallery`)}>
              Gallery
            </Button>
            <Button variant="primary" onClick={() => void toggleFullscreen()}>
              Fullscreen (F)
            </Button>
          </div>
        </header>
      )}

      {captures.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(255,255,255,0.5)',
            fontSize: 20,
          }}
        >
          Waiting for the first photo…
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 12,
            flex: 1,
          }}
        >
          {captures.map((c) => (
            <Tile
              key={c.id}
              capture={c}
              fresh={recentlyAddedIds.has(c.id)}
            />
          ))}
        </div>
      )}

      {qrVisible && (
        <QrOverlay
          url={`${window.location.origin}/${slug}`}
          title={manifest?.title ?? slug}
          onClose={() => setQrVisible(false)}
        />
      )}
    </div>
  );
}

function Tile({ capture, fresh }: { capture: BoothCaptureSummary; fresh: boolean }) {
  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: fresh ? '0 0 0 3px #ff3a8c, 0 0 28px rgba(255,58,140,0.55)' : 'none',
        transform: fresh ? 'scale(1.02)' : 'scale(1)',
        transition: 'transform 600ms ease, box-shadow 1800ms ease',
        animation: fresh ? 'wall-pop-in 480ms ease' : undefined,
      }}
    >
      {capture.kind === 'photo' ? (
        <img
          src={capture.mediaUrl}
          alt=""
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <video
          src={capture.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <style>{`
        @keyframes wall-pop-in {
          0% { opacity: 0; transform: scale(0.7); }
          70% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#000',
        color: '#fff',
        display: 'grid',
        placeItems: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
